// Partograma (051, Fase 4 Tanda 2a) — gráfico dilatación cervical × horas
// de registro con curva de alerta p10 superpuesta. Geometría fija del
// formulario impreso (grilla 0-14h × 1-10cm) + una capa de interacción —
// mismo criterio ya usado para decidir que Denver II/odontograma son
// HTML/SVG bespoke, no el motor de `recharts` de Fase 1 (ver PLAN.md).
//
// Simplificación deliberada: un punto de dilatación por hora de registro
// (clic en la intersección hora×cm más cercana), no coordenadas libres —
// el propio papel imprime los puntos exactamente sobre las líneas de hora,
// así que esto no pierde fidelidad frente al formulario original.
import { type JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  curvaAlertaPartograma,
  filaDeFcf,
  PARIDAD_MEMBRANA_OPCIONES,
  PLANOS_HODGE_PARTOGRAMA,
  TIPOS_DIP_PARTOGRAMA,
  type DipPartograma,
  type MembranasPartograma,
  type ParidadPartograma,
  type PlanoHodgePartograma,
  type PuntoFcfPartograma,
  type PuntoHodgePartograma,
  type TipoDipPartograma,
} from '@logiacare/hce-pdf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const HORA_MAX = 14;
const DILATACION_MIN = 1;
const DILATACION_MAX = 10;
// Una fila más arriba del 10: es el 200 de la escala FCF del papel (100 lpm en el 1, +10 lpm por fila).
const FILA_MAX = 11;
/** Los puntos pueden ir en la línea de cada hora o a la media hora (puntos intermedios). */
const PASO_HORA = 0.5;
const CONTENT_W = 700;
const CONTENT_H = 270;
const CELDA_W = 24;
const CELDA_H = 22;
const EJE_Y_W = 28;

function xPx(hora: number): number {
  return (hora / HORA_MAX) * CONTENT_W;
}

function yPx(cm: number): number {
  return CONTENT_H - ((cm - DILATACION_MIN) / (FILA_MAX - DILATACION_MIN)) * CONTENT_H;
}

const HORAS = Array.from({ length: HORA_MAX + 1 }, (_, i) => i);
const HORAS_CON_MEDIAS = Array.from({ length: HORA_MAX / PASO_HORA + 1 }, (_, i) => i * PASO_HORA);
const FILAS_EJE = Array.from({ length: FILA_MAX - DILATACION_MIN + 1 }, (_, i) => FILA_MAX - i);
const DILATACIONES = Array.from({ length: DILATACION_MAX - DILATACION_MIN + 1 }, (_, i) => DILATACION_MAX - i);
const PARIDADES = [...new Set(PARIDAD_MEMBRANA_OPCIONES.map((o) => o.paridad))];

function membranasValidas(paridad: ParidadPartograma): readonly MembranasPartograma[] {
  return PARIDAD_MEMBRANA_OPCIONES.filter((o) => o.paridad === paridad).map((o) => o.membranas);
}

export interface EstadoPartogramaVisual051 {
  readonly puntos: Readonly<Record<number, number>>;
  readonly horaBaseLineaAlerta: string;
  readonly paridad: ParidadPartograma | '';
  readonly membranas: MembranasPartograma | '';
  /** Se dibujan sobre la misma grilla que la dilatación (eje derecho 100-200 lpm). */
  readonly fcf: readonly PuntoFcfPartograma[];
  readonly hodge: readonly PuntoHodgePartograma[];
  readonly dips: readonly DipPartograma[];
  /** Hora de registro de la ruptura espontánea (REM) / artificial (RAM) de membranas; null = no registrada. */
  readonly rem: number | null;
  readonly ram: number | null;
}

export function estadoInicialPartogramaVisual051(): EstadoPartogramaVisual051 {
  return { puntos: {}, horaBaseLineaAlerta: '0', paridad: '', membranas: '', fcf: [], hodge: [], dips: [], rem: null, ram: null };
}

/** Un solo valor de FCF/plano de Hodge por hora: registrar de nuevo en la misma hora lo reemplaza. Ordenado por hora. */
export function registrarPorHora<T extends { readonly hora: number }>(lista: readonly T[], nuevo: T): T[] {
  return [...lista.filter((x) => x.hora !== nuevo.hora), nuevo].sort((a, b) => a.hora - b.hora);
}

/** Un dip por (hora, tipo): repetirlo no lo duplica. */
export function registrarDip(lista: readonly DipPartograma[], nuevo: DipPartograma): DipPartograma[] {
  if (lista.some((d) => d.hora === nuevo.hora && d.tipo === nuevo.tipo)) return [...lista];
  return [...lista, nuevo].sort((a, b) => a.hora - b.hora);
}

export const FCF_REGISTRABLE_MIN = 50;
export const FCF_REGISTRABLE_MAX = 250;

/** FCF en lpm: entero dentro de un rango fisiológicamente registrable; `undefined` si el texto no es válido. */
export function leerFcf(texto: string): number | undefined {
  if (!/^\d{2,3}$/.test(texto.trim())) return undefined;
  const n = Number(texto);
  return n >= FCF_REGISTRABLE_MIN && n <= FCF_REGISTRABLE_MAX ? n : undefined;
}

type TipoEvento = 'fcf' | 'hodge' | 'dip' | 'rem' | 'ram';
const TIPOS_EVENTO: readonly TipoEvento[] = ['fcf', 'hodge', 'dip', 'rem', 'ram'];

/** Un punto (hora entera) por columna — clic de nuevo sobre el mismo cm lo quita. Exportada para test, mismo criterio que `asignarCarriles` en DenverIIVisual.tsx. */
export function alternarPuntoPartograma(puntos: Readonly<Record<number, number>>, hora: number, cm: number): Record<number, number> {
  const resultado = { ...puntos };
  if (resultado[hora] === cm) {
    delete resultado[hora];
  } else {
    resultado[hora] = cm;
  }
  return resultado;
}

export interface PartogramaVisualProps {
  readonly estado: EstadoPartogramaVisual051;
  readonly onChange: (estado: EstadoPartogramaVisual051) => void;
}

export function PartogramaVisual({ estado, onChange }: PartogramaVisualProps): JSX.Element {
  const { t } = useTranslation();

  const horaBaseNum = Number(estado.horaBaseLineaAlerta);
  const curva = useMemo(() => {
    if (!estado.paridad || !estado.membranas || Number.isNaN(horaBaseNum)) return [];
    return curvaAlertaPartograma(horaBaseNum, estado.paridad, estado.membranas);
  }, [estado.paridad, estado.membranas, horaBaseNum]);

  const puntosOrdenados = Object.entries(estado.puntos)
    .map(([hora, dilatacion]) => ({ hora: Number(hora), dilatacion }))
    .sort((a, b) => a.hora - b.hora);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-hce-surface p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="f051-partograma-paridad">{t('formularios.f051.partograma.paridad')}</Label>
          <Select value={estado.paridad || undefined} onValueChange={(v) => onChange({ ...estado, paridad: v as ParidadPartograma, membranas: '' })}>
            <SelectTrigger id="f051-partograma-paridad">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PARIDADES.map((p) => (
                <SelectItem key={p} value={p}>
                  {t(`formularios.f051.partograma.paridadOpciones.${p}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="f051-partograma-membranas">{t('formularios.f051.partograma.membranas')}</Label>
          <Select value={estado.membranas || undefined} onValueChange={(v) => onChange({ ...estado, membranas: v as MembranasPartograma })} disabled={!estado.paridad}>
            <SelectTrigger id="f051-partograma-membranas">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(estado.paridad ? membranasValidas(estado.paridad) : []).map((m) => (
                <SelectItem key={m} value={m}>
                  {t(`formularios.f051.partograma.membranasOpciones.${m}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="f051-partograma-hora-base">{t('formularios.f051.partograma.horaBaseLineaAlerta')}</Label>
          <Input
            id="f051-partograma-hora-base"
            value={estado.horaBaseLineaAlerta}
            onChange={(e) => onChange({ ...estado, horaBaseLineaAlerta: e.target.value })}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="relative" style={{ width: CONTENT_W + EJE_Y_W + 80, height: CONTENT_H + 24 }}>
          {FILAS_EJE.map((fila) => (
            <span key={fila} className="absolute -translate-y-1/2 text-[11px] font-medium text-hce-muted" style={{ left: 0, top: yPx(fila) }}>
              {fila <= DILATACION_MAX ? fila : ''}
            </span>
          ))}
          {FILAS_EJE.map((fila) => (
            <span key={`fcf-${fila}`} className="absolute -translate-y-1/2 text-[10px] text-hce-muted" style={{ left: EJE_Y_W + CONTENT_W + 4, top: yPx(fila) }}>
              {100 + (fila - DILATACION_MIN) * 10}
            </span>
          ))}
          {PLANOS_HODGE_PARTOGRAMA.map((p) => (
            <span key={p.plano} className="absolute -translate-y-1/2 text-[10px] font-medium text-hce-muted" style={{ left: EJE_Y_W + CONTENT_W + 34, top: yPx(filaDeFcf(p.fcfEquivalente)) }}>
              {p.plano} · {p.deLee}
            </span>
          ))}
          <div className="absolute" style={{ left: EJE_Y_W, top: 0, width: CONTENT_W, height: CONTENT_H }}>
            <svg className="pointer-events-none absolute" width={CONTENT_W} height={CONTENT_H} viewBox={`0 0 ${CONTENT_W} ${CONTENT_H}`}>
              {HORAS.map((h) => (
                <line key={`v-${h}`} x1={xPx(h)} y1={0} x2={xPx(h)} y2={CONTENT_H} stroke="var(--border)" strokeWidth={0.5} />
              ))}
              {FILAS_EJE.map((cm) => (
                <line key={`h-${cm}`} x1={0} y1={yPx(cm)} x2={CONTENT_W} y2={yPx(cm)} stroke="var(--border)" strokeWidth={0.5} />
              ))}
              {curva.length > 1 && (
                <polyline
                  points={curva.map((p) => `${xPx(p.hora)},${yPx(p.dilatacion)}`).join(' ')}
                  fill="none"
                  stroke="var(--color-hce-clay)"
                  strokeWidth={1.5}
                  strokeDasharray="4,3"
                />
              )}
              {puntosOrdenados.length > 1 && (
                <polyline points={puntosOrdenados.map((p) => `${xPx(p.hora)},${yPx(p.dilatacion)}`).join(' ')} fill="none" stroke="var(--color-hce-pine)" strokeWidth={2} />
              )}
              {puntosOrdenados.map((p) => (
                <circle key={p.hora} cx={xPx(p.hora)} cy={yPx(p.dilatacion)} r={4} fill="var(--color-hce-pine)" />
              ))}
              {estado.fcf.length > 1 && (
                <polyline points={estado.fcf.map((p) => `${xPx(p.hora)},${yPx(filaDeFcf(p.fcf))}`).join(' ')} fill="none" stroke="var(--color-hce-muted)" strokeWidth={1} />
              )}
              {estado.fcf.map((p) => (
                <rect key={`fcf-${p.hora}`} x={xPx(p.hora) - 3.5} y={yPx(filaDeFcf(p.fcf)) - 3.5} width={7} height={7} fill="none" stroke="var(--color-hce-ink)" strokeWidth={1.5} />
              ))}
              {estado.hodge.map((p) => {
                const plano = PLANOS_HODGE_PARTOGRAMA.find((h) => h.plano === p.plano);
                if (!plano) return null;
                const cy = yPx(filaDeFcf(plano.fcfEquivalente));
                return (
                  <g key={`hodge-${p.hora}`} stroke="var(--color-hce-ink)" strokeWidth={1.2} fill="none">
                    <circle cx={xPx(p.hora)} cy={cy} r={5} />
                    <line x1={xPx(p.hora)} y1={cy - 7} x2={xPx(p.hora)} y2={cy + 7} />
                  </g>
                );
              })}
              {estado.dips.map((d) => (
                <text key={`dip-${d.hora}-${d.tipo}`} x={xPx(d.hora) - 3} y={CONTENT_H - 4} fontSize={11} fill="var(--color-hce-clay)">
                  {d.tipo}
                </text>
              ))}
              {estado.rem !== null && (
                <text x={xPx(estado.rem) + 2} y={12} fontSize={11} fill="var(--color-hce-clay)">
                  REM
                </text>
              )}
              {estado.ram !== null && (
                <text x={xPx(estado.ram) + 2} y={24} fontSize={11} fill="var(--color-hce-clay)">
                  RAM
                </text>
              )}
            </svg>
            {HORAS_CON_MEDIAS.map((hora) =>
              DILATACIONES.map((cm) => (
                <button
                  key={`${hora}-${cm}`}
                  type="button"
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full outline-none hover:bg-hce-pine/10 focus-visible:bg-hce-pine/15"
                  style={{ left: xPx(hora), top: yPx(cm), width: CELDA_W, height: CELDA_H }}
                  onClick={() => onChange({ ...estado, puntos: alternarPuntoPartograma(estado.puntos, hora, cm) })}
                  aria-label={t('formularios.f051.partograma.celdaAria', { hora, cm })}
                />
              )),
            )}
          </div>
          {HORAS.map((h) => (
            <span key={h} className="absolute -translate-x-1/2 text-[11px] font-medium text-hce-muted" style={{ left: EJE_Y_W + xPx(h), top: CONTENT_H + 6 }}>
              {h}
            </span>
          ))}
        </div>
      </div>
      <p className="text-xs text-hce-muted">{t('formularios.f051.partograma.instrucciones')}</p>
      <EventosPartograma estado={estado} onChange={onChange} />
    </div>
  );
}

const CLASE_SELECT = 'h-9 rounded-md border border-input bg-transparent px-2 text-sm';

/** Registro de lo que el papel dibuja sobre la grilla además de la dilatación: FCF (lpm), plano de Hodge + variedad de posición, dips (I/II/V/M) y REM/RAM. */
function EventosPartograma({ estado, onChange }: PartogramaVisualProps): JSX.Element {
  const { t } = useTranslation();
  const p = 'formularios.f051.partograma.eventos';
  const [tipo, setTipo] = useState<TipoEvento>('fcf');
  const [hora, setHora] = useState('0');
  const [fcf, setFcf] = useState('');
  const [plano, setPlano] = useState<PlanoHodgePartograma>('I');
  const [variedad, setVariedad] = useState('');
  const [dip, setDip] = useState<TipoDipPartograma>('I');

  const horaNum = Number(hora);
  const fcfNum = leerFcf(fcf);
  const puedeAgregar = tipo !== 'fcf' || fcfNum !== undefined;

  function agregar(): void {
    if (tipo === 'fcf' && fcfNum !== undefined) {
      onChange({ ...estado, fcf: registrarPorHora(estado.fcf, { hora: horaNum, fcf: fcfNum }) });
      setFcf('');
    } else if (tipo === 'hodge') {
      const v = variedad.trim();
      onChange({ ...estado, hodge: registrarPorHora(estado.hodge, { hora: horaNum, plano, ...(v === '' ? {} : { variedad: v }) }) });
      setVariedad('');
    } else if (tipo === 'dip') {
      onChange({ ...estado, dips: registrarDip(estado.dips, { hora: horaNum, tipo: dip }) });
    } else if (tipo === 'rem') {
      onChange({ ...estado, rem: horaNum });
    } else if (tipo === 'ram') {
      onChange({ ...estado, ram: horaNum });
    }
  }

  const lineas: { readonly clave: string; readonly texto: string; readonly accion: () => void }[] = [
    ...estado.fcf.map((x) => ({ clave: `fcf-${x.hora}`, texto: t(`${p}.lineaFcf`, { hora: x.hora, fcf: x.fcf }), accion: () => onChange({ ...estado, fcf: estado.fcf.filter((y) => y.hora !== x.hora) }) })),
    ...estado.hodge.map((x) => ({
      clave: `hodge-${x.hora}`,
      texto: t(`${p}.lineaHodge`, { hora: x.hora, plano: x.plano, variedad: x.variedad ?? '—' }),
      accion: () => onChange({ ...estado, hodge: estado.hodge.filter((y) => y.hora !== x.hora) }),
    })),
    ...estado.dips.map((x) => ({
      clave: `dip-${x.hora}-${x.tipo}`,
      texto: t(`${p}.lineaDip`, { hora: x.hora, tipo: x.tipo }),
      accion: () => onChange({ ...estado, dips: estado.dips.filter((y) => !(y.hora === x.hora && y.tipo === x.tipo)) }),
    })),
    ...(estado.rem === null ? [] : [{ clave: 'rem', texto: t(`${p}.lineaRem`, { hora: estado.rem }), accion: () => onChange({ ...estado, rem: null }) }]),
    ...(estado.ram === null ? [] : [{ clave: 'ram', texto: t(`${p}.lineaRam`, { hora: estado.ram }), accion: () => onChange({ ...estado, ram: null }) }]),
  ];

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <p className="text-sm font-medium text-hce-ink">{t(`${p}.titulo`)}</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="f051-partograma-evento-tipo">{t(`${p}.tipo`)}</Label>
          <select id="f051-partograma-evento-tipo" className={CLASE_SELECT} value={tipo} onChange={(e) => setTipo(e.target.value as TipoEvento)}>
            {TIPOS_EVENTO.map((o) => (
              <option key={o} value={o}>
                {t(`${p}.tipoOpciones.${o}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="f051-partograma-evento-hora">{t(`${p}.hora`)}</Label>
          <select id="f051-partograma-evento-hora" className={CLASE_SELECT} value={hora} onChange={(e) => setHora(e.target.value)}>
            {HORAS_CON_MEDIAS.map((h) => (
              <option key={h} value={String(h)}>
                {h}
              </option>
            ))}
          </select>
        </div>
        {tipo === 'fcf' && (
          <div className="flex flex-col gap-1">
            <Label htmlFor="f051-partograma-evento-fcf">{t(`${p}.fcf`)}</Label>
            <Input id="f051-partograma-evento-fcf" className="w-24" inputMode="numeric" value={fcf} onChange={(e) => setFcf(e.target.value)} />
          </div>
        )}
        {tipo === 'hodge' && (
          <>
            <div className="flex flex-col gap-1">
              <Label htmlFor="f051-partograma-evento-plano">{t(`${p}.plano`)}</Label>
              <select id="f051-partograma-evento-plano" className={CLASE_SELECT} value={plano} onChange={(e) => setPlano(e.target.value as PlanoHodgePartograma)}>
                {PLANOS_HODGE_PARTOGRAMA.map((o) => (
                  <option key={o.plano} value={o.plano}>
                    {o.plano} (De Lee {o.deLee})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="f051-partograma-evento-variedad">{t(`${p}.variedad`)}</Label>
              <Input id="f051-partograma-evento-variedad" className="w-28" maxLength={20} value={variedad} onChange={(e) => setVariedad(e.target.value)} />
            </div>
          </>
        )}
        {tipo === 'dip' && (
          <div className="flex flex-col gap-1">
            <Label htmlFor="f051-partograma-evento-dip">{t(`${p}.dip`)}</Label>
            <select id="f051-partograma-evento-dip" className={CLASE_SELECT} value={dip} onChange={(e) => setDip(e.target.value as TipoDipPartograma)}>
              {TIPOS_DIP_PARTOGRAMA.map((o) => (
                <option key={o} value={o}>
                  {t(`${p}.dipOpciones.${o}`)}
                </option>
              ))}
            </select>
          </div>
        )}
        <Button type="button" onClick={agregar} disabled={!puedeAgregar}>
          {t(`${p}.agregar`)}
        </Button>
      </div>
      {tipo === 'fcf' && fcf.trim() !== '' && fcfNum === undefined && (
        <p role="alert" className="text-xs text-destructive">
          {t(`${p}.fcfInvalida`, { min: FCF_REGISTRABLE_MIN, max: FCF_REGISTRABLE_MAX })}
        </p>
      )}
      {lineas.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm">
          {lineas.map((l) => (
            <li key={l.clave} className="flex items-center justify-between gap-2">
              <span>{l.texto}</span>
              <Button type="button" size="sm" variant="outline" onClick={l.accion} aria-label={t(`${p}.quitarAria`, { que: l.texto })}>
                {t(`${p}.quitar`)}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
